import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  type TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, type OverlayRef, type PositionStrategy } from '@angular/cdk/overlay';

import { TOOLTIP_DATA, TooltipComponent } from '@core/tooltip/tooltip.component';

@Directive({
  selector: '[tessTooltip]',
  host: {
    '(mouseenter)': 'focus()',
    '(mouseleave)': 'blur()',
  },
})
export class TooltipDirective implements OnDestroy {
  #element = inject<ElementRef<HTMLElement>>(ElementRef);
  #overlay = inject(Overlay);
  #viewContainer = inject(ViewContainerRef);
  tessTooltip = input.required<TemplateRef<void> | string>();
  tooltipDelay = input(500);
  tooltipDuration = input(500);
  #overlayRef: OverlayRef | null = null;
  #showTimeout: ReturnType<typeof setTimeout>;
  #hideTimeout: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    this.#overlayRef?.dispose();
  }

  focus(): void {
    if (this.#overlayRef?.hasAttached()) {
      clearTimeout(this.#hideTimeout);
      return;
    }

    this.#showTimeout = setTimeout(() => {
      this.#attachTooltip();
    }, this.tooltipDelay());
  }

  blur(): void {
    if (!this.#overlayRef?.hasAttached()) {
      clearTimeout(this.#showTimeout);
      return;
    }

    this.#hideTimeout = setTimeout(() => {
      if (this.#overlayRef?.hasAttached()) {
        this.#overlayRef?.detach();
      }
    }, this.tooltipDuration());
  }

  #attachTooltip(): void {
    if (this.#overlayRef === null) {
      const positionStrategy = this.#getPositionStrategy();
      this.#overlayRef = this.#overlay.create({ positionStrategy });
    }

    const key = typeof this.tessTooltip() === 'string' ? 'text' : 'template';

    const injector = Injector.create({
      providers: [
        {
          provide: TOOLTIP_DATA,
          useValue: {
            focus: (): void => this.focus(),
            blur: (): void => this.blur(),
            [key]: this.tessTooltip(),
          },
        },
      ],
    });
    const component = new ComponentPortal(TooltipComponent, this.#viewContainer, injector);
    this.#overlayRef.attach(component);
  }

  #getPositionStrategy(): PositionStrategy {
    return this.#overlay
      .position()
      .flexibleConnectedTo(this.#element)
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          panelClass: 'top',
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          panelClass: 'bottom',
        },
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          panelClass: 'end',
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          panelClass: 'start',
        },
      ]);
  }
}
